import { MemoryModel, MediaModel, Contact, ContactModel, GroupModel, getMediaType } from "common247";

export interface IMemoryFilterCriteria {
  /** Text search matching title or description */
  searchQuery?: string;
  /** Start date filter (ISO string, dd-MMM-yyyy, or timestamp) */
  dateFrom?: string | number | Date | null;
  /** End date filter (ISO string, dd-MMM-yyyy, or timestamp) */
  dateTo?: string | number | Date | null;
  /** Selected contact IDs */
  contactIds?: string[];
  /** Selected group IDs */
  groupIds?: string[];
  /** Selected tag IDs */
  tagIds?: string[];
  /** Memory category or narrative type */
  memoryType?: string;
  /** Filter by media types */
  mediaTypes?: ("all" | "image" | "video" | "audio" | "document" | "pdf" | "other")[];
  /** Minimum favourite rating (1 to 5) */
  minRating?: number;
  /** Whether to only show memories with media highlights */
  onlyWithMedia?: boolean;
}

export interface IFilteredEntityResultSet {
  memories: MemoryModel[];
  media: MediaModel[];
  contacts: (Contact | ContactModel)[];
  groups: GroupModel[];
  totalCounts: {
    memories: number;
    media: number;
    contacts: number;
    groups: number;
  };
  criteria: IMemoryFilterCriteria;
}

/**
 * Parses date string or timestamp into a normalized Unix epoch timestamp.
 */
export function parseDateToEpoch(val: any): number | null {
  if (!val) return null;
  if (typeof val === "number") {
    // If it's a 4-digit year e.g. 2026
    if (val > 1900 && val < 2100) {
      return new Date(val, 0, 1).getTime();
    }
    return val;
  }
  if (val instanceof Date) return val.getTime();

  const str = String(val).trim();
  // Check 4-digit year string
  if (/^\d{4}$/.test(str)) {
    return new Date(parseInt(str, 10), 0, 1).getTime();
  }

  const parsed = Date.parse(str);
  if (!isNaN(parsed)) return parsed;

  // Handle dd-MMM-yyyy e.g. 15-Jun-2026
  const parts = str.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
  if (parts) {
    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const mIdx = months.indexOf(parts[2].toLowerCase());
    if (mIdx !== -1) {
      return new Date(parseInt(parts[3], 10), mIdx, parseInt(parts[1], 10)).getTime();
    }
  }

  return null;
}

/**
 * Calculates duration in days for a memory. Defaults to 1 day if single date or undated.
 *
 * @param {MemoryModel} memory - Memory to measure.
 * @returns {number} Duration in days (minimum 1).
 */
export function calculateMemoryDurationDays(memory: MemoryModel): number {
  if (!memory) return 1;

  // If date contains a span e.g. "01-Jan-2026 to 05-Jan-2026"
  if (typeof memory.date === "string" && memory.date.includes(" to ")) {
    const [startStr, endStr] = memory.date.split(" to ");
    const startEpoch = parseDateToEpoch(startStr);
    const endEpoch = parseDateToEpoch(endStr);
    if (startEpoch && endEpoch && endEpoch >= startEpoch) {
      const diffDays = Math.round((endEpoch - startEpoch) / (1000 * 60 * 60 * 24)) + 1;
      return Math.max(1, diffDays);
    }
  }

  // If memory has startDate and endDate fields
  const anyMem = memory as any;
  if (anyMem.startDate && anyMem.endDate) {
    const startEpoch = parseDateToEpoch(anyMem.startDate);
    const endEpoch = parseDateToEpoch(anyMem.endDate);
    if (startEpoch && endEpoch && endEpoch >= startEpoch) {
      const diffDays = Math.round((endEpoch - startEpoch) / (1000 * 60 * 60 * 24)) + 1;
      return Math.max(1, diffDays);
    }
  }

  return 1;
}

/**
 * Calculates the time difference in days between two memories.
 *
 * @param {MemoryModel} memA - First memory.
 * @param {MemoryModel} memB - Second memory.
 * @returns {number} Difference in days (minimum 0).
 */
export function calculateMemoryTimeDifferenceDays(memA: MemoryModel, memB: MemoryModel): number {
  const epochA = parseDateToEpoch(memA.date) || memA.createdAt || 0;
  const epochB = parseDateToEpoch(memB.date) || memB.createdAt || 0;
  if (!epochA || !epochB) return 1;

  const diffMs = Math.abs(epochA - epochB);
  return Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
}

/**
 * Applies multi-dimensional filtering criteria across Memories, Media, Contacts, and Groups.
 *
 * @param {MemoryModel[]} allMemories - Source memories list.
 * @param {MediaModel[]} allMedia - Source media items.
 * @param {(Contact | ContactModel)[]} allContacts - Source contacts.
 * @param {GroupModel[]} allGroups - Source groups.
 * @param {IMemoryFilterCriteria} criteria - Filter criteria.
/**
 * Safely converts an input into an array, handling arrays, objects/maps, and null/undefined.
 */
function toSafeArray<T>(val: any): T[] {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "object") return Object.values(val) as T[];
  return [];
}

/**
 * Safely extracts a Set of string IDs from arrays, sets, or object keys/values.
 */
function toSafeIdSet(val: any): Set<string> {
  if (!val) return new Set<string>();
  if (Array.isArray(val)) return new Set<string>(val.filter(Boolean).map(String));
  if (val instanceof Set) return new Set<string>(Array.from(val).filter(Boolean).map(String));
  if (typeof val === "object") {
    return new Set<string>(Object.keys(val).filter(Boolean).map(String));
  }
  return new Set<string>();
}

/**
 * Applies multi-dimensional filtering across memories, media, contacts, and groups.
 *
 * @param {MemoryModel[]} allMemories - Source memories.
 * @param {MediaModel[]} allMedia - Source media items.
 * @param {(Contact | ContactModel)[]} allContacts - Source contacts.
 * @param {GroupModel[]} allGroups - Source groups.
 * @param {IMemoryFilterCriteria} criteria - Filter criteria.
 * @returns {IFilteredEntityResultSet} The filtered entity result set.
 */
export function applyMemoryFilter(
  allMemories: MemoryModel[] = [],
  allMedia: MediaModel[] = [],
  allContacts: (Contact | ContactModel)[] = [],
  allGroups: GroupModel[] = [],
  criteria: IMemoryFilterCriteria = {}
): IFilteredEntityResultSet {
  const safeMemories = toSafeArray<MemoryModel>(allMemories);
  const safeMedia = toSafeArray<MediaModel>(allMedia);
  const safeContacts = toSafeArray<Contact | ContactModel>(allContacts);
  const safeGroups = toSafeArray<GroupModel>(allGroups);

  const safeCriteria = criteria || {};
  const query = (safeCriteria.searchQuery || "").toLowerCase().trim();
  const fromEpoch = parseDateToEpoch(safeCriteria.dateFrom);
  const toEpoch = parseDateToEpoch(safeCriteria.dateTo);
  const selectedContactIds = toSafeIdSet(safeCriteria.contactIds);
  const selectedGroupIds = toSafeIdSet(safeCriteria.groupIds);
  const selectedTagIds = toSafeIdSet(safeCriteria.tagIds);

  const matchingMemories = safeMemories.filter((mem) => {
    if (!mem) return false;
    // 1. Text Search Filter
    if (query) {
      const titleMatch = (mem.title || "").toLowerCase().includes(query);
      const descMatch = (mem.description || "").toLowerCase().includes(query);
      const locationText = typeof mem.location === "string" ? mem.location : (mem.location?.text || (mem.location as any)?.label || "");
      const locationMatch = locationText.toLowerCase().includes(query);
      if (!titleMatch && !descMatch && !locationMatch) {
        return false;
      }
    }

    // 2. Date Range Filter
    const memEpoch = parseDateToEpoch(mem.date) || mem.createdAt || null;
    if (memEpoch) {
      if (fromEpoch !== null && memEpoch < fromEpoch) return false;
      if (toEpoch !== null && memEpoch > toEpoch) return false;
    }

    // 3. Contacts Filter
    if (selectedContactIds.size > 0) {
      const contactIdList: string[] = [];
      if (Array.isArray((mem as any).contactIds)) {
        contactIdList.push(...(mem as any).contactIds);
      }
      if (Array.isArray((mem as any).sharedContactIds)) {
        contactIdList.push(...(mem as any).sharedContactIds);
      }
      if (Array.isArray(mem.contactItems)) {
        mem.contactItems.forEach((c) => {
          if (c && c.id) contactIdList.push(c.id);
        });
      }
      if (mem.contacts && typeof mem.contacts === "object" && !Array.isArray(mem.contacts)) {
        contactIdList.push(...Object.keys(mem.contacts));
      }
      const memContactIds = new Set(contactIdList);
      let hasMatchingContact = false;
      for (const cId of selectedContactIds) {
        if (memContactIds.has(cId)) {
          hasMatchingContact = true;
          break;
        }
      }
      if (!hasMatchingContact) return false;
    }

    // 4. Groups Filter
    if (selectedGroupIds.size > 0) {
      const groupIdList: string[] = [];
      if (Array.isArray((mem as any).groupIds)) {
        groupIdList.push(...(mem as any).groupIds);
      }
      if (Array.isArray((mem as any).sharedGroupIds)) {
        groupIdList.push(...(mem as any).sharedGroupIds);
      }
      if (Array.isArray(mem.groupItems)) {
        mem.groupItems.forEach((g) => {
          if (g && g.id) groupIdList.push(g.id);
        });
      }
      if (mem.groups && typeof mem.groups === "object" && !Array.isArray(mem.groups)) {
        groupIdList.push(...Object.keys(mem.groups));
      }
      const memGroupIds = new Set(groupIdList);
      let hasMatchingGroup = false;
      for (const gId of selectedGroupIds) {
        if (memGroupIds.has(gId)) {
          hasMatchingGroup = true;
          break;
        }
      }
      if (!hasMatchingGroup) return false;
    }

    // 5. Tags Filter
    if (selectedTagIds.size > 0) {
      const tagIdList: string[] = [];
      if (Array.isArray((mem as any).tagIds)) {
        tagIdList.push(...(mem as any).tagIds);
      }
      if (mem.tags && typeof mem.tags === "object" && !Array.isArray(mem.tags)) {
        tagIdList.push(...Object.keys(mem.tags));
      }
      const memTagIds = new Set(tagIdList);
      let hasMatchingTag = false;
      for (const tId of selectedTagIds) {
        if (memTagIds.has(tId)) {
          hasMatchingTag = true;
          break;
        }
      }
      if (!hasMatchingTag) return false;
    }

    // 6. Memory Type Filter
    if (criteria.memoryType && criteria.memoryType !== "all") {
      const memType = (mem as any).type || (mem as any).memoryType || "standard";
      if (memType.toLowerCase() !== criteria.memoryType.toLowerCase()) {
        return false;
      }
    }

    // 7. Media Presence / Highlights
    if (criteria.onlyWithMedia) {
      const hasMedia =
        (Array.isArray(mem.mediaItems) && mem.mediaItems.length > 0) ||
        (Array.isArray(mem.mediaIds) && mem.mediaIds.length > 0) ||
        (mem.media && typeof mem.media === "object" && !Array.isArray(mem.media) && Object.keys(mem.media).length > 0) ||
        (Array.isArray((mem as any).media) && (mem as any).media.length > 0) ||
        Boolean(mem.photoMediaId);
      if (!hasMedia) {
        return false;
      }
    }

    // 8. Favourite Rating filter
    if (criteria.minRating && criteria.minRating > 1) {
      const rating = mem.favouriteRating ?? (mem as any).rating ?? 3;
      if (rating < criteria.minRating) {
        return false;
      }
    }

    return true;
  });

  // Collect associated media IDs and embedded media from matching memories
  const matchingMemoryIds = new Set(matchingMemories.map((m) => m.id).filter(Boolean) as string[]);
  const associatedMediaMap = new Map<string, MediaModel>();

  // Add embedded media from memories
  for (const mem of matchingMemories) {
    if (Array.isArray(mem.mediaItems)) {
      for (const item of mem.mediaItems) {
        if (item && item.id) {
          associatedMediaMap.set(item.id, item);
        }
      }
    } else if (Array.isArray((mem as any).media)) {
      for (const item of (mem as any).media) {
        if (item && item.id) {
          associatedMediaMap.set(item.id, item);
        }
      }
    }
  }

  // Also include standalone media items linked to matching memories
  const memoryLinkedMediaIds = new Set<string>();
  for (const mem of matchingMemories) {
    if (Array.isArray(mem.mediaIds)) {
      mem.mediaIds.forEach((id) => memoryLinkedMediaIds.add(id));
    }
    if (mem.media && typeof mem.media === "object" && !Array.isArray(mem.media)) {
      Object.keys(mem.media).forEach((id) => memoryLinkedMediaIds.add(id));
    }
    if (mem.photoMediaId) {
      memoryLinkedMediaIds.add(mem.photoMediaId);
    }
  }

  for (const m of safeMedia) {
    const memoryId = (m as any).memoryId || (m.metadata as any)?.memory_id;
    const isDirectMemoryLink =
      (memoryId && matchingMemoryIds.has(memoryId)) ||
      (Array.isArray(m.memoryIds) && m.memoryIds.some((id) => matchingMemoryIds.has(id))) ||
      (m.memories && typeof m.memories === "object" && Object.keys(m.memories).some((id) => matchingMemoryIds.has(id)));
    const isContainedInMatchingMemory = m.id ? memoryLinkedMediaIds.has(m.id) : false;
    if ((isDirectMemoryLink || isContainedInMatchingMemory) && m.id) {
      if (!associatedMediaMap.has(m.id)) {
        associatedMediaMap.set(m.id, m);
      }
    }
  }

  // Filter associated media by criteria (mediaTypes and rating)
  const criteriaMediaTypes = Array.isArray(safeCriteria.mediaTypes)
    ? safeCriteria.mediaTypes
    : safeCriteria.mediaTypes
    ? [safeCriteria.mediaTypes]
    : undefined;

  const filteredMedia = Array.from(associatedMediaMap.values()).filter((item) => {
    // Media Type filter
    if (criteriaMediaTypes && criteriaMediaTypes.length > 0 && !criteriaMediaTypes.includes("all")) {
      const cType = (item.contentType || "").toLowerCase();
      const detected = item.mediaType || getMediaType(cType, item.name);
      const matchesType = criteriaMediaTypes.some((t) => {
        if (t === "image") return detected === "image" || cType.startsWith("image/");
        if (t === "video") return detected === "video" || cType.startsWith("video/");
        if (t === "audio") return detected === "audio" || cType.startsWith("audio/");
        if (t === "pdf") return detected === "pdf";
        if (t === "document") return detected === "document";
        if (t === "other") return detected === "other";
        return true;
      });
      if (!matchesType) return false;
    }

    // Min rating filter
    if (safeCriteria.minRating && safeCriteria.minRating > 1) {
      const rating = (item as any).favouriteRating || (item as any).rating || 1;
      if (rating < safeCriteria.minRating) return false;
    }

    return true;
  });

  // Resolve associated contacts
  const associatedContactIds = new Set<string>();
  for (const mem of matchingMemories) {
    if (Array.isArray(mem.contactItems)) {
      mem.contactItems.forEach((c) => {
        if (c && c.id) associatedContactIds.add(c.id);
      });
    }
    const cIds = (mem as any).contactIds;
    if (Array.isArray(cIds)) {
      cIds.forEach((id: string) => associatedContactIds.add(id));
    }
    if (mem.contacts && typeof mem.contacts === "object" && !Array.isArray(mem.contacts)) {
      Object.keys(mem.contacts).forEach((id) => associatedContactIds.add(id));
    }
  }
  const filteredContacts = safeContacts.filter((c) =>
    selectedContactIds.size > 0 ? selectedContactIds.has(c.id!) : associatedContactIds.has(c.id!) || safeMemories.length === 0
  );

  // Resolve associated groups
  const associatedGroupIds = new Set<string>();
  for (const mem of matchingMemories) {
    if (Array.isArray(mem.groupItems)) {
      mem.groupItems.forEach((g) => {
        if (g && g.id) associatedGroupIds.add(g.id);
      });
    }
    const gIds = (mem as any).groupIds;
    if (Array.isArray(gIds)) {
      gIds.forEach((id: string) => associatedGroupIds.add(id));
    }
    if (mem.groups && typeof mem.groups === "object" && !Array.isArray(mem.groups)) {
      Object.keys(mem.groups).forEach((id) => associatedGroupIds.add(id));
    }
  }
  const filteredGroups = safeGroups.filter((g) =>
    selectedGroupIds.size > 0 ? selectedGroupIds.has(g.id!) : associatedGroupIds.has(g.id!) || safeMemories.length === 0
  );

  return {
    memories: matchingMemories,
    media: filteredMedia,
    contacts: filteredContacts,
    groups: filteredGroups,
    totalCounts: {
      memories: matchingMemories.length,
      media: filteredMedia.length,
      contacts: filteredContacts.length,
      groups: filteredGroups.length,
    },
    criteria,
  };
}
